"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EntityWithCompanyId = exports.EntityWithId = exports.getEntityHelper = exports.getStaticEntityHelper = exports.EntityDecorator = exports.isEntitySymbol = exports.listFilterSymbolValue = exports.searchFieldSymbolValue = exports.field = exports.EntityMutedError = exports.ENTITY_UNMUTED_EVENT = void 0;
exports.ENTITY_UNMUTED_EVENT = 'unMutedEvent';
class Entity {
    // eslint-disable-next-line no-useless-constructor,no-empty-function
    constructor(currentValues, scope) {
    }
}
class EntityMutedError extends Error {
    constructor(details) {
        super(`${details.tableName}:${String(details.id)} entity is muted, can not update '${String(details.field)}' from '${String(details.from)}' to '${String(details.to)}'`);
        this.details = details;
    }
}
exports.EntityMutedError = EntityMutedError;
const isFieldValueEqual = (fieldToCheck, a, b) => {
    if (fieldToCheck.isJSON) {
        return JSON.stringify(a) === JSON.stringify(b);
    }
    return a === b;
};
/* property decorator */
function field(extraArgs) {
    return function fieldDecorator(target, propertyKey, _descriptor) {
        const currentField = { ...extraArgs, name: propertyKey };
        target.constructor.fields = [...(target.constructor.fields || [])];
        target.constructor.fields.push(currentField);
        const symbol = Symbol(propertyKey);
        const foreignSymbol = Symbol(`f_${propertyKey}`);
        target.constructor._fieldSymbols = { ...(target.constructor._fieldSymbols || {}) };
        target.constructor._fieldSymbols[propertyKey] = symbol;
        target.constructor._foreignFieldSymbols = { ...(target.constructor._foreignFieldSymbols || {}) };
        target.constructor._foreignFieldSymbols[propertyKey] = foreignSymbol;
        target.constructor.fieldSymbolValues = { ...(target.constructor.fieldSymbolValues || {}) };
        target.constructor.fieldSymbolValues[symbol] = propertyKey;
        target.constructor.fieldSymbolValues[foreignSymbol] = propertyKey;
        Object.defineProperty(target, propertyKey, {
            set(newValue) {
                const oldValue = this.dataValues[propertyKey];
                const { processor } = extraArgs;
                const processedValue = processor ? processor(newValue) : newValue;
                if (isFieldValueEqual(currentField, oldValue, processedValue)) {
                    return;
                }
                if (this.isMuted()) {
                    this.owner.constructor.reportError(new EntityMutedError({
                        tableName: target.constructor.name,
                        id: this.dataValues.id,
                        field: propertyKey,
                        from: this.dataValues[propertyKey],
                        to: processedValue,
                    }));
                }
                this.dataValues[propertyKey] = processedValue;
                if (this.isOriginalFieldName(propertyKey)) {
                    this.isChanged = true;
                    this.changedFields[propertyKey] = true;
                }
                if (this.suppressFire === false) {
                    this.onFieldChanged.call(this.owner, propertyKey, processedValue, oldValue);
                }
            },
            get() {
                return this.dataValues[propertyKey];
            },
        });
    };
}
exports.field = field;
exports.searchFieldSymbolValue = Symbol('searchField');
exports.listFilterSymbolValue = Symbol('listFilter');
exports.isEntitySymbol = Symbol('isEntitySmybol');
function EntityDecorator(constructor) {
    var _a, _b;
    return _b = class DecoratedEntity extends constructor {
            get [(_a = exports.isEntitySymbol, exports.listFilterSymbolValue)]() {
                return this.dataValues[exports.listFilterSymbolValue];
            }
            set [exports.listFilterSymbolValue](value) {
                this.dataValues[exports.listFilterSymbolValue] = value;
            }
            get [exports.searchFieldSymbolValue]() {
                return this.dataValues[exports.searchFieldSymbolValue];
            }
            set [exports.searchFieldSymbolValue](value) {
                this.dataValues[exports.searchFieldSymbolValue] = value;
            }
            static getFieldSymbols() {
                return this._fieldSymbols;
            }
            static getForeignFieldSymbols() {
                return this._foreignFieldSymbols;
            }
            static hasField(fieldName) {
                return this.fields.some(fieldToCheck => fieldToCheck.name === fieldName);
            }
            constructor(...args) {
                super(...args);
                this.isChanged = false;
                this.changedFields = {};
                this.suppressFire = false;
                this.muted = false;
                this.dataValues = {};
                this.onFieldChanged = (key, newValue, oldValue) => { };
                const currentValues = args[0];
                if (currentValues instanceof constructor) {
                    // @ts-ignore
                    // eslint-disable-next-line no-constructor-return
                    return currentValues;
                }
                this.owner = args[1];
                const { fields } = this.constructor;
                if (!Array.isArray(fields)) {
                    throw new Error('Entity class should define fields by @field descriptor');
                }
                //
                // Don't fire change event at initialisation
                //
                this.suppressEvents();
                //
                // Init the fields
                //
                [...fields].forEach(fieldDefinition => {
                    this.dataValues[fieldDefinition.name] = this.constructor.getTypedFieldValue(fieldDefinition, currentValues[fieldDefinition.name]);
                });
                this.isChanged = false;
                this.unSuppressEvents();
            }
            static getTypedFieldValue(fieldDefinition, value) {
                if (fieldDefinition.isJSON && typeof value === 'string') {
                    try {
                        return JSON.parse(value);
                    }
                    catch (e) {
                        return fieldDefinition.defaultValue || {};
                    }
                }
                if (fieldDefinition.type === 'boolean' && value !== undefined) {
                    return Boolean(value);
                }
                return value;
            }
            set(valueObject) {
                Object.assign(this, valueObject);
            }
            /**
             * Indicates whether the entity is new. An entity is
             * considered as new when its ID field's value is a symbol
             *
             * @returns {boolean}
             */
            isNew() {
                return typeof this.dataValues.id === 'symbol';
            }
            /**
             * Indicates whether the entity is updated. An entity is
             * considered as updated when any of its fields changed
             * and the entity itself is not new
             *
             * @returns {boolean}
             */
            isUpdated() {
                return this.isChanged && !this.isNew();
            }
            setDirty() {
                this.isChanged = true;
            }
            getDataValues() {
                return this.dataValues;
            }
            /**
             * Get dataValues without any Symbol included. This is important, as we are cloning `dataValues` when creating new
             * {ComplexData} objects. The symbols may have references to deep object structures and to the {ExtCache}. This
             * could make the cloning process exponentially slower as we use the app and filling the {ExtCache}. Also looks like
             * quite a big memory hog.
             *
             * This function, getDataValues and any related functionality should be reviewed under card PN-6630.
             *
             */
            getPureDataValues() {
                return Object.keys(this.dataValues).reduce((values, key) => Object.assign(values, {
                    [key]: this.dataValues[key],
                }), {});
            }
            getEntries() {
                return Object.entries(this.dataValues);
            }
            /**
             * Mute the entity making sure no changes allowed on it.
             * If a muted entity is being changed then an assertion is thrown
             */
            mute() {
                this.muted = true;
            }
            /**
             * unmute the entity allowing changes on its data
             */
            unmute() {
                this.muted = false;
                this.owner.emit(exports.ENTITY_UNMUTED_EVENT);
            }
            async waitForUnmute() {
                if (!this.isMuted()) {
                    return;
                }
                await new Promise(resolve => {
                    this.owner.once(exports.ENTITY_UNMUTED_EVENT, resolve);
                });
            }
            /**
             * Checks whether the entity is muted
             *
             * @returns {boolean}
             */
            isMuted() {
                return this.muted;
            }
            /**
             * Mute the entity making sure that change events
             * are not fired until it is restored by calling
             * unSuppressEvents()
             */
            suppressEvents() {
                this.suppressFire = true;
            }
            /**
             * Unmute the entity and makes sure change events
             * are fired
             */
            unSuppressEvents() {
                this.suppressFire = false;
            }
            /**
             * Setter function for object properties
             *
             * @param key
             * @param processor
             * @param newValue
             */
            getChangedFields() {
                return Object.keys(this.changedFields).reduce((result, key) => {
                    result[key] = this.dataValues[key];
                    return result;
                }, {});
            }
            isOriginalFieldName(key) {
                const { fields } = this.constructor;
                return fields.some(fieldDef => fieldDef.name === key);
            }
        },
        _b[_a] = true,
        _b.searchFilterSymbol = exports.searchFieldSymbolValue,
        _b.listFilterSymbol = exports.listFilterSymbolValue,
        _b.EVENTS = {
            FIELD_CHANGED: 'fieldChanged',
        },
        _b;
}
exports.EntityDecorator = EntityDecorator;
function getStaticEntityHelper(entity) {
    return entity;
}
exports.getStaticEntityHelper = getStaticEntityHelper;
function getEntityHelper(entity) {
    return entity;
}
exports.getEntityHelper = getEntityHelper;
exports.default = Entity;
class EntityWithId extends Entity {
}
exports.EntityWithId = EntityWithId;
class EntityWithCompanyId extends EntityWithId {
}
exports.EntityWithCompanyId = EntityWithCompanyId;
//# sourceMappingURL=../../src/entity.js.map